import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RenderIf } from 'utilities/helpers';
import { PrimaryButton } from 'components/button';
import { TextField } from 'components/input';
import { AXIOS_INSTANCE_ODS } from 'utilities/db';
import { Toasts } from 'components/toasts';
import { useStore } from 'store';
import { useCountry } from 'contexts/CountryContext';
import { Loading } from 'components/loading';
import styles from './index.module.css';

export const PaymentSettingsListing = () => {
  const { branch, user } = useStore((state) => state);
  const { countryCode } = useCountry();
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [hasPaymentSettings, setHasPaymentSettings] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    defaultValues: {
      bankName: '',
      accountHolderName: '',
      accountIbanNumber: '',
      accountNumber: ''
    }
  });

  // Fetch payment settings on mount
  useEffect(() => {
    if (branch?.id && countryCode === 'PK') {
      fetchPaymentSettings();
    }
  }, [branch?.id, countryCode]);

  const fetchPaymentSettings = async () => {
    try {
      setIsLoading(true);
      // Fetch existing payment settings - using the same endpoint to check if exists
      // This might need to be adjusted based on actual GET endpoint
      const response = await AXIOS_INSTANCE_ODS.get(
        `/merchant/getJazzCashIdentityBranch/${branch?.id}`
      );
      const data = response?.data?.result?.merchant?.jazzcash_info;

      if (data && data?.bank_name) {
        setHasPaymentSettings(true);
        setValue('bankName', data?.bank_name || '');
        setValue('accountHolderName', data?.account_holder_name || '');
        setValue('accountIbanNumber', data?.iban || '');
        setValue('accountNumber', data?.account_number || '');
      } else {
        setHasPaymentSettings(false);
        reset();
      }
    } catch (error) {
      // If 404 or no data, show empty form
      if (error?.response?.status === 404) {
        setHasPaymentSettings(false);
        reset();
      } else {
        // Silently fail for GET - might not have settings yet
        setHasPaymentSettings(false);
        reset();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    try {
      setSubmitLoading(true);
      const payload = {
        external_id: `${branch?.id}`,
        payment_provider: 'JAZZCASH',
        level: 'BRANCH',
        bank_name: formData.bankName,
        account_holder_name: formData.accountHolderName,
        account_number: formData.accountNumber,
        iban: formData.accountIbanNumber
      };

      // Use the same endpoint for both create and update
      await AXIOS_INSTANCE_ODS.post(
        `/merchant/createJazzCashIdentity`,
        payload
      );

      Toasts.success(
        hasPaymentSettings
          ? 'Payment settings updated successfully'
          : 'Payment settings saved successfully'
      );
      setHasPaymentSettings(true);
    } catch (error) {
      Toasts.error(
        error?.response?.data?.message || 'Failed to save payment settings'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // Don't show if not PK country
  if (countryCode !== 'PK') {
    return null;
  }

  return (
    <article className={styles.article}>
      <header className={styles.header}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800' }}>
            Payment Settings
          </h2>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            Manage your bank account details for franchise partnership payments
          </p>
        </div>
      </header>

      <RenderIf isTrue={isLoading}>
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}
        >
          <Loading />
        </div>
      </RenderIf>

      <RenderIf isTrue={!isLoading}>
        <RenderIf isTrue={!hasPaymentSettings}>
          <div className={styles.disclaimerBox}>
            <div className={styles.disclaimerIcon}>ℹ️</div>
            <div className={styles.disclaimerContent}>
              <h6 className={styles.disclaimerTitle}>
                Franchise Partnership Information
              </h6>
              <p className={styles.disclaimerText}>
                Please add your bank account details below if you have a
                franchise partnership with the brand. This information will be
                used for payment processing and financial transactions.
              </p>
            </div>
          </div>
        </RenderIf>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.formSection}>
            <TextField
              label="Bank Name"
              name="bankName"
              id="bankName"
              placeholder="Enter bank name"
              error={
                errors?.bankName?.type === 'required'
                  ? 'Bank name is required'
                  : ''
              }
              register={register}
              validation={{
                required: true
              }}
              required
            />

            <div className={styles.fieldSpacing} />

            <TextField
              label="Account Holder Name"
              name="accountHolderName"
              id="accountHolderName"
              placeholder="Enter account holder name"
              error={
                errors?.accountHolderName?.type === 'required'
                  ? 'Account holder name is required'
                  : ''
              }
              register={register}
              validation={{
                required: true
              }}
              required
            />

            <div className={styles.fieldSpacing} />

            <TextField
              label="Account IBAN Number"
              name="accountIbanNumber"
              id="accountIbanNumber"
              placeholder="PK00XXXX0000000000000000"
              error={
                errors?.accountIbanNumber?.type === 'required'
                  ? 'IBAN number is required'
                  : errors?.accountIbanNumber?.type === 'pattern'
                  ? 'Please enter a valid IBAN number'
                  : ''
              }
              register={register}
              validation={{
                required: true,
                pattern: /^PK[0-9]{2}[A-Z]{4}[0-9]{16}$/i
              }}
              required
            />

            <div className={styles.fieldSpacing} />

            <TextField
              label="Account Number"
              name="accountNumber"
              id="accountNumber"
              placeholder="Enter account number"
              error={
                errors?.accountNumber?.type === 'required'
                  ? 'Account number is required'
                  : errors?.accountNumber?.type === 'pattern'
                  ? 'Please enter a valid account number'
                  : ''
              }
              register={register}
              validation={{
                required: true,
                pattern: /^[0-9]+$/
              }}
              required
            />
          </div>

          <footer className={styles.footer}>
            <PrimaryButton
              type="submit"
              disabled={submitLoading}
              isLoading={submitLoading}
            >
              {hasPaymentSettings ? 'Update Settings' : 'Save Settings'}
            </PrimaryButton>
          </footer>
        </form>
      </RenderIf>
    </article>
  );
};
